"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AccessRoutes = exports.AccessRoleRoutes = exports.AccessMeReplyV1 = exports.AccessMeQueryV1 = exports.AccessCatalogReplyV1 = exports.AccessCatalogQueryV1 = exports.AccessCatalogResourceV1 = exports.RoleEntity = exports.RoleErrorCodes = exports.RoleDomain = exports.RoleOverrideMode = exports.RoleAudience = exports.RoleCode = void 0;
const zod_1 = require("zod");
const contracts_core_1 = require("@omniflow/contracts-core");
const contracts_base_1 = require("@omniflow/contracts-base");
// ── Domain ──
/**
 * Роль (эпик ACCESS, `docs/todo-access.md` O5/O6/O15/O22) — per-tenant набор галочек
 * каталога `@omniflow/access`: пресет-база + отличия. Effective-права считаются на
 * каждом resolve (merge on read), поэтому капабилити, добавленная в пресет кодом,
 * доезжает до роли без миграций.
 *
 * Код роли — бизнес-метка (O17): его пишут в `core.users.roles`, и стратегии
 * сравнивают его строкой. Меняется у своих ролей (O24), назначения не переписываются.
 */
exports.RoleCode = zod_1.z.string().trim().min(1).max(64).regex(/^[a-z0-9]+([-_][a-z0-9]+)*$/);
/** Кому роль назначается (O22): человеку или стратегии (через токен). */
exports.RoleAudience = zod_1.z.enum(['human', 'strategy']);
/** Отличие ячейки от пресета: включить или выключить. Нет ключа — наследуется. */
exports.RoleOverrideMode = zod_1.z.enum(['allow', 'deny']);
exports.RoleDomain = zod_1.z.object({
    /**
     * Бизнес-метка роли (O17): её назначают пользователям и токенам, по ней роль узнают
     * сценарии. Меняется у своих ролей (O24); назначения при этом НЕ переписываются —
     * связь мягкая (O23): старый код у пользователей перестаёт давать права этой роли.
     * У встроенных ролей не меняется (`builtin_immutable`).
     */
    code: exports.RoleCode,
    name: zod_1.z.string().trim().min(1).max(128),
    /**
     * Включена ли роль (O24). Выключенная хранит галочки и назначения, но прав не даёт.
     * Не передано на create — включена. Встроенные не выключаются (`builtin_immutable`).
     */
    isActive: zod_1.z.boolean().optional(),
    /** Неизменяема после create (`audience_immutable`). */
    audience: exports.RoleAudience,
    /** Код пресета каталога (`operator`/`supervisor`/`admin`); `null` — роль с нуля. */
    basePreset: zod_1.z.string().trim().min(1).max(64).nullable().optional(),
    /** Отличия от пресета: `<resource>:<action>` → allow|deny. Пусто — ровно пресет. */
    overrides: zod_1.z.record(zod_1.z.string().min(1).max(128), exports.RoleOverrideMode).optional(),
    /**
     * Коды ролей, которыми управляет держатель этой роли (O26): правит их и раздаёт
     * пользователям и токенам. Галочки при этом любые — граница только по кодам.
     * Связь мягкая, как у назначений: кода может ещё не быть. Новая роль сама попадает
     * в список ролей создателя, дающих право создавать роли. Встроенный admin управляет
     * всеми. Только у ролей людей.
     */
    managesRoles: zod_1.z.array(exports.RoleCode).max(256).optional(),
    /**
     * Код роли Actenso (ACTPANEL O5), с которым держателю роли выдаётся токен панели
     * ИИ-ассистента. Задан ⇔ у роли право `assistant:use`: без кода право не сохранить
     * (`actenso_role_code`), снятие права очищает код. Допустимость кода проверяет Actenso.
     */
    actensoRoleCode: exports.RoleCode.nullable().optional(),
    /**
     * Состав пресета на момент последнего сохранения — UI показывает «новое в пресете»
     * как разницу с текущим пресетом. Пишет сервер (O5/O18).
     */
    presetSeen: (0, contracts_base_1.server)(zod_1.z.array(zod_1.z.string()).nullable()),
    /** Встроенная роль (operator/supervisor/admin): неудаляема, код зарезервирован (O15). */
    isBuiltin: (0, contracts_base_1.server)(zod_1.z.boolean()),
});
/**
 * Коды ошибок сверх CRUD-базы. Отказы правил безопасности — отдельными кодами, чтобы
 * UI объяснял причину, а не показывал «validation_error».
 */
exports.RoleErrorCodes = (0, contracts_base_1.extendCrudErrorCodes)(
// `code_immutable` снят: код роли меняется (O24).
'audience_immutable', 'reserved_code', 'builtin_immutable', 'unknown_capability', 'unknown_preset', 'missing_requires', 'platform_capability', 
/** Роль вне списка `manages_roles` актора (O26). */
'not_managed', 'system_tenant', 
/** Право `assistant:use` без кода роли Actenso или код без права (ACTPANEL O5). */
'actenso_role_code');
// ── Entity ──
exports.RoleEntity = (0, contracts_base_1.defineEntity)('access.role', exports.RoleDomain, {
    errorCodes: exports.RoleErrorCodes,
    list: { maxLimit: 1_000 },
});
// ── Каталог для редактора ──
const CapabilityRefV1 = zod_1.z.object({
    /** `<resource>:<action>` — стабильный id (O20). */
    id: zod_1.z.string(),
    title: zod_1.z.string(),
    sensitivity: zod_1.z.enum(['normal', 'secret']),
    /** Раздаёт права (пользователи, токены, роли): роли стратегии не выдаётся до STRATACC (O26). */
    humanOnly: zod_1.z.boolean(),
    scope: zod_1.z.enum(['tenant', 'platform']),
    requires: zod_1.z.array(zod_1.z.string()),
    implies: zod_1.z.array(zod_1.z.string()),
    /** Гранты — для подсказки «ещё выдаётся через …» (пересечения, O2). Без секретов. */
    pub: zod_1.z.array(zod_1.z.string()),
    sub: zod_1.z.array(zod_1.z.string()),
});
exports.AccessCatalogResourceV1 = zod_1.z.object({
    code: zod_1.z.string(),
    title: zod_1.z.string(),
    section: zod_1.z.string(),
    audience: exports.RoleAudience,
    crud: zod_1.z.object({
        read: CapabilityRefV1.optional(),
        create: CapabilityRefV1.optional(),
        update: CapabilityRefV1.optional(),
        delete: CapabilityRefV1.optional(),
    }),
    /**
     * Фичи строки; `guard` — право без subject'ов, которое проверяет handler/REST;
     * `menu` — пункт меню (O27): только видимость страницы, прав не даёт.
     */
    features: zod_1.z.array(CapabilityRefV1.extend({ action: zod_1.z.string(), guard: zod_1.z.boolean(), menu: zod_1.z.boolean() })),
});
exports.AccessCatalogQueryV1 = contracts_core_1.QueryMessage.extend({
    type: zod_1.z.literal('AccessCatalogQuery'),
    version: zod_1.z.literal(1),
});
exports.AccessCatalogReplyV1 = zod_1.z.object({
    sections: zod_1.z.array(zod_1.z.object({ code: zod_1.z.string(), title: zod_1.z.string() })),
    resources: zod_1.z.array(exports.AccessCatalogResourceV1),
    /** Пресет → капабилити (без раскрытия implies — UI раскрывает сам по `implies`). */
    presets: zod_1.z.record(zod_1.z.string(), zod_1.z.array(zod_1.z.string())),
    /**
     * Показывать ли раздел «Платформа»: только в тенанте `system` (O8). В остальных
     * тенантах platform-строки из ответа уже вычищены.
     */
    platformVisible: zod_1.z.boolean(),
});
// ── Мои права ──
exports.AccessMeQueryV1 = contracts_core_1.QueryMessage.extend({
    type: zod_1.z.literal('AccessMeQuery'),
    version: zod_1.z.literal(1),
});
exports.AccessMeReplyV1 = zod_1.z.discriminatedUnion('isOk', [
    zod_1.z.object({
        isOk: zod_1.z.literal(true),
        /** Коды ролей пользователя (как в `core.users.roles`). */
        roles: zod_1.z.array(zod_1.z.string()),
        /** Фактические капабилити (с раскрытым implies) — по ним UI строит меню и гарды. */
        capabilities: zod_1.z.array(zod_1.z.string()),
        /** Встроенный admin (`>`): все страницы, включая не описанные каталогом. */
        superuser: zod_1.z.boolean(),
        /**
         * Коды ролей, которыми пользователь управляет (O26) — объединение `managesRoles`
         * его ролей. У superuser — все роли тенанта, список не перечисляет.
         */
        managesRoles: zod_1.z.array(zod_1.z.string()),
    }),
    zod_1.z.object({ isOk: zod_1.z.literal(false), errorCode: zod_1.z.string(), error: zod_1.z.string().optional() }),
]);
// ── Routes ──
exports.AccessRoleRoutes = {
    ...exports.RoleEntity.routes,
};
exports.AccessRoutes = {
    /** Каталог капабилити + пресеты для редактора ролей. */
    catalog: (0, contracts_core_1.defineQuery)({ subject: 'q.access.catalog', input: exports.AccessCatalogQueryV1, reply: exports.AccessCatalogReplyV1 }),
    /**
     * Права текущего пользователя (актор — server-stamped identity, не поле запроса).
     * Выдаётся любому человеку: роль без галочек всё равно должна войти и увидеть
     * «нет доступа» (O21).
     */
    me: (0, contracts_core_1.defineQuery)({ subject: 'q.access.me', input: exports.AccessMeQueryV1, reply: exports.AccessMeReplyV1 }),
};
//# sourceMappingURL=routes.js.map